import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { authSchema } from "@/lib/validation";
import { useRateLimit, RATE_LIMIT_PRESETS } from "@/hooks/useRateLimit";
import { EmailVerificationPending } from "./EmailVerificationPending";
import { z } from "zod";
import { lovable } from "@/integrations/lovable/index";
import {
  attemptPasswordSignInFallback,
  isNetworkFetchError,
} from "@/lib/authNetworkRecovery";

export default function AuthForm() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState(false);
  const [pendingVerification, setPendingVerification] = useState(false);
  const [pendingEmail, setPendingEmail] = useState("");
  const { toast } = useToast();
  const { checkLimit, isLimited } = useRateLimit(RATE_LIMIT_PRESETS.auth);

  const handleNetworkFailure = async () => {
    toast({
      title: "Login failed",
      description: "Could not reach the server. Please check your connection and try again.",
      variant: "destructive",
    });
    setLoading(false);
    return "failed" as const;
  };

  const waitForSessionUser = async (retries = 6, delayMs = 250) => {
    for (let attempt = 0; attempt < retries; attempt += 1) {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (session?.user) {
        return session.user;
      }

      await new Promise((resolve) => window.setTimeout(resolve, delayMs));
    }

    return null;
  };

  const tryPasswordSignInFallback = async () => {
    const fallbackResult = await attemptPasswordSignInFallback(email, password);
    const existingSessionUser = await waitForSessionUser();

    if (fallbackResult.ok || existingSessionUser) {
      toast({
        title: "Success!",
        description: "Logged in successfully.",
      });
      setLoading(false);
      return true;
    }

    if (
      "isNetworkError" in fallbackResult &&
      !fallbackResult.isNetworkError &&
      fallbackResult.errorMessage
    ) {
      toast({
        title: "Login failed",
        description: fallbackResult.errorMessage,
        variant: "destructive",
      });
      setLoading(false);
      return true;
    }

    return false;
  };

  const handleAppleSignIn = async () => {
    setOauthLoading(true);

    try {
      const result = await lovable.auth.signInWithOAuth("apple", {
        redirect_uri: window.location.origin,
      });

      if (result.error) {
        const existingSessionUser = await waitForSessionUser();
        if (!existingSessionUser) {
          toast({
            title: "Error",
            description: result.error.message || "Apple sign-in failed",
            variant: "destructive",
          });
        }
      }
    } finally {
      setOauthLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setOauthLoading(true);

    try {
      const result = await lovable.auth.signInWithOAuth("google", {
        redirect_uri: window.location.origin,
      });

      if (result.error) {
        const existingSessionUser = await waitForSessionUser();
        if (!existingSessionUser) {
          toast({
            title: "Error",
            description: result.error.message || "Google sign-in failed",
            variant: "destructive",
          });
        }
      }
    } finally {
      setOauthLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!checkLimit()) {
      return;
    }

    setLoading(true);

    try {
      authSchema.parse({ email, password });
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({
          title: "Validation Error",
          description: error.errors[0].message,
          variant: "destructive",
        });
        setLoading(false);
        return;
      }
    }

    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/`,
        },
      });

      if (error) {
        if (isNetworkFetchError(error)) {
          await handleNetworkFailure();
          return;
        }
        toast({
          title: "Sign up failed",
          description: error.message,
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      if (data?.user) {
        if (data.user.identities?.length === 0) {
          toast({
            title: "Account exists",
            description: "This email is already registered. Please sign in instead.",
            variant: "destructive",
          });
        } else if (!data.session) {
          setPendingEmail(email);
          setPendingVerification(true);
          toast({
            title: "Almost there! 📧",
            description: "Check your email to verify your account.",
          });
        } else {
          toast({
            title: "Welcome to Tax.GBT! 🎉",
            description: "Let's get started...",
          });
        }
      }
    } catch (err: unknown) {
      if (isNetworkFetchError(err)) {
        await handleNetworkFailure();
        return;
      }
      const message = err instanceof Error ? err.message : "Unexpected sign up error";
      toast({
        title: "Sign up failed",
        description: message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!checkLimit()) {
      return;
    }

    setLoading(true);

    try {
      authSchema.shape.email.parse(email);
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({
          title: "Validation Error",
          description: error.errors[0].message,
          variant: "destructive",
        });
        setLoading(false);
        return;
      }
    }

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        if (isNetworkFetchError(error)) {
          const fallbackHandled = await tryPasswordSignInFallback();
          if (fallbackHandled) return;
          await handleNetworkFailure();
          return;
        }

        if (error.message.includes("Email not confirmed")) {
          setPendingEmail(email);
          setPendingVerification(true);
          toast({
            title: "Email not verified",
            description: "Please check your inbox and verify your email first.",
            variant: "destructive",
          });
        } else {
          toast({
            title: "Login failed",
            description: error.message,
            variant: "destructive",
          });
        }
        setLoading(false);
        return;
      }

      toast({
        title: "Success!",
        description: "Logged in successfully.",
      });
    } catch (err: unknown) {
      if (isNetworkFetchError(err)) {
        const fallbackHandled = await tryPasswordSignInFallback();
        if (fallbackHandled) return;
        await handleNetworkFailure();
        return;
      }

      const message = err instanceof Error ? err.message : "Unexpected login error";
      toast({
        title: "Login failed",
        description: message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setPendingVerification(false);
    setPendingEmail("");
    setEmail("");
    setPassword("");
  };

  // Show verification pending screen
  if (pendingVerification) {
    return (
      <EmailVerificationPending 
        email={pendingEmail} 
        onBackToLogin={handleBackToLogin} 
      />
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-white p-4">
      <Card className="w-full max-w-md p-8">
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold text-primary mb-2">Tax.GBT 🚀</h1>
          <p className="text-muted-foreground">Your AI tax expert who talks simple, saves you money, and makes taxes fun.</p>
          <p className="text-sm mt-2 font-medium">
            You've probably been overpaying hundreds 😱 Let's fix that!
          </p>
        </div>

        <Tabs defaultValue="login" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="login">Login</TabsTrigger>
            <TabsTrigger value="signup">Sign Up</TabsTrigger>
          </TabsList>

          <TabsContent value="login">
            <form onSubmit={handleSignIn} className="space-y-4">
              <div>
                <Input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div>
                <Input
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading || isLimited()}>
                {loading ? "Loading..." : "Log In"}
              </Button>
              <div className="relative my-2">
                <div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div>
                <div className="relative flex justify-center text-xs uppercase"><span className="bg-card px-2 text-muted-foreground">or</span></div>
              </div>
              <Button
                type="button"
                variant="outline"
                className="w-full gap-2"
                disabled={oauthLoading}
                onClick={handleAppleSignIn}
              >
                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor"><path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/></svg>
                {oauthLoading ? "Loading..." : "Sign in with Apple"}
              </Button>
              <Button
                type="button"
                variant="outline"
                className="w-full gap-2"
                disabled={oauthLoading}
                onClick={handleGoogleSignIn}
              >
                <svg viewBox="0 0 24 24" className="h-5 w-5"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                {oauthLoading ? "Loading..." : "Sign in with Google"}
              </Button>
            </form>
          </TabsContent>

          <TabsContent value="signup">
            <form onSubmit={handleSignUp} className="space-y-4">
              <div>
                <Input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div>
                <Input
                  type="password"
                  placeholder="Password (min 8 characters, uppercase, lowercase, number, special char)"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Must include: uppercase, lowercase, number, and special character
                </p>
              </div>
              <Button type="submit" className="w-full" disabled={loading || isLimited()}>
                {loading ? "Loading..." : "Sign Up"}
              </Button>
              <div className="relative my-2">
                <div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div>
                <div className="relative flex justify-center text-xs uppercase"><span className="bg-card px-2 text-muted-foreground">or</span></div>
              </div>
              <Button
                type="button"
                variant="outline"
                className="w-full gap-2"
                disabled={oauthLoading}
                onClick={handleAppleSignIn}
              >
                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor"><path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/></svg>
                {oauthLoading ? "Loading..." : "Sign up with Apple"}
              </Button>
              <Button
                type="button"
                variant="outline"
                className="w-full gap-2"
                disabled={oauthLoading}
                onClick={handleGoogleSignIn}
              >
                <svg viewBox="0 0 24 24" className="h-5 w-5"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                {oauthLoading ? "Loading..." : "Sign up with Google"}
              </Button>
              <p className="text-xs text-center text-muted-foreground">
                You'll receive a verification email to confirm your account
              </p>
            </form>
          </TabsContent>
        </Tabs>
      </Card>
    </div>
  );
}
